/* eslint-disable no-unused-vars */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { autorun } from 'mobx';
import Modal from '@tencent/dui/lib/components/Modal';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { isLogin } from 'utils';
import i18n from '@i18next/i18n';
import ua from '@tencent/docs-user-agent';
import { PPTEditorStateEnumType, PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { ITipsManager, ITipsManagerInjectable } from '@src/slide/application/service/tips-manager/core/tips-manager.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import { IHistoryManager } from '@src/slide/application/service/history-manager/core/history-manager.interface';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IAnonymousViewService } from '@src/slide/application/service/anonymous-view-service';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';

interface ITipsManagerProps {
    selectionService: ISelectionService;
}

/**
 * tips弹窗
 * 之后迁移到基础设施层
 */
export default class TipsManager implements ITipsManager {
    private hasShownCannotEditTips = false;
    private isShowingConvertException = false;
    private convertErrorElementMap: Map<string, boolean>;
    private userPriviledgeService: IUserPriviledgeService | undefined;
    private selectionService: ISelectionService;
    private historyManager?: IHistoryManager;
    private anonymousViewService: IAnonymousViewService;

    public constructor(props: ITipsManagerProps, private injectable?: ITipsManagerInjectable) {
        const { selectionService } = props;
        this.convertErrorElementMap = new Map();
        this.selectionService = selectionService;
    }

    public setHistoryManagerDelegate(historyManager: IHistoryManager): void {
        if (!this.historyManager) {
            this.historyManager = historyManager;
        }
    }

    public showCannotCopyTips(): void {
        if (this.injectable?.showCannotCopyTips) {
            return this.injectable?.showCannotCopyTips(this);
        }
        this.showSnackbar({
            message: i18n.t('文档管理员限制了查看者的操作权限，无法复制内容。'),
            autoClose: true,
            closable: false,
        });
    }

    public showMsg(message, duration = 1000): void {
        this.showSnackbar({
            message,
            duration,
        });
    }

    public showBrowserNotSupport(): void {
        if (this.injectable?.showBrowserNotSupport) {
            return this.injectable.showBrowserNotSupport(this);
        }

        const shortcuts = ua.isMac ? '⌘+V' : 'Ctrl+V';
        this.showSnackbar({
            message: i18n.t('请使用快捷键 {0} 进行粘贴', { 0: shortcuts }),
            autoClose: true,
            closable: true,
        });
    }

    public showSnackbar(configs): void {
        try {
            if (ua.isMobile) {
                import('@tencent/dui-mobile/lib/components/Snackbar').then((obj) => {
                    const SnackbarMobile = obj.default;
                    SnackbarMobile.show(configs);
                });
            } else {
                import('@tencent/dui/lib/components/Snackbar').then((obj) => {
                    const Snackbar = obj.default;
                    Snackbar.show(configs);
                });
            }
        } catch (error) {
            window.log.error(`[loadChunk-error]${error}`);
        }
    }

    /**
     * 查看态不可以编辑弹出的Tips，只弹出一次
     * @param callBack
     */
    // eslint-disable-next-line complexity
    public showCannotEditTips(): void {
        if (this.injectable?.showCannotEditTips && !this.injectable.showCannotEditTips(this)) {
            return;
        }
        // 修订记录不弹tips，被非会员协同编辑限制，列表页还在分页加载，不弹tips
        if (this.historyManager?.isShowingHistory || this.userPriviledgeService?.isOverCoorperateNum
            || this.selectionService.currentSlide?.preload) {
            return;
        }
        if (this.selectionService?.slides?.length > 0) {
            const lockManager = OnlineEditServices.CooperationLockManager();
            const lockInfo = lockManager?.getLockInfoByIndex(
                this.selectionService.currentSlideIndex,
                this.selectionService.currentSlideType,
            );
            if (lockInfo && !lockInfo.user.isSelf) {
                SlideDataServices.SlideAppService.promise().then((slideAppService) => {
                    const slideCount: number = slideAppService.getSlideCount();
                    if (slideCount === 1) {
                        this.showSnackbar({
                            message: i18n.t('该页正在被其他用户编辑中，请稍后尝试。'),
                            duration: 5000,
                        });
                    } else {
                        this.showSnackbar({
                            message: i18n.t('该页正在被其他用户编辑中，请稍后尝试或编辑其他页。'),
                            duration: 5000,
                        });
                    }
                });
                return;
            }
        }

        if (this.isShowAnonymousModal()) {
            this.anonymousViewService.showAnonymousEditModal();
            return;
        }

        // 用户具备文档权限，但被业务限制了编辑器状态; 此时不应弹出无权限提示
        // TODO: need refactor
        if (this.isEditorStateChangedByOther()) return;

        if (!this.hasShownCannotEditTips) {
            this.hasShownCannotEditTips = true;
            if (!isLogin()) {
                eventBus.emit(EventKeys.SHOW_LOGIN_GUIDE_PC);
            } else {
                eventBus.emit(EventKeys.SHOW_AUTH_GUIDE_PC);
            }
        }
    }

    /**
     * SDK数据转换为UI数据过程中发生了error或者exception，上报错误数据并给出提示
     * @param modelInfo 转换出错的model信息
     * @param e 异常信息
     */
    public showConvertExceptionModal(modelInfo, e): void {
        if (e.message && e.stack) {
            console.error('ConvertException (SDK -> UI):', e);
            window.log.info(`convert sdk to ui exception: ${e.message} + ${e.stack}`);
        }
        if (modelInfo.key && !this.convertErrorElementMap.get(modelInfo.key)) {
            this.convertErrorElementMap.set(modelInfo.key, true);
            const info = JSON.stringify({
                errorMsg: e.message,
                type: modelInfo.type,
                inGroup: modelInfo.inGroup,
                slideIndex: modelInfo.slideIndex,
            });
            tdw({
                opername: 'quality_slide',
                module: 'detail',
                action: 'convert_sdk2ui_error',
                ver6: info,
            });
            if (!this.isShowingConvertException) {
                // eslint-disable-next-line @typescript-eslint/no-this-alias
                const self = this;
                self.isShowingConvertException = true;
                setTimeout(() => {
                    Modal.confirm({
                        title: i18n.t('提示'),
                        content: i18n.t('文档中有部分数据损坏，该内容不可编辑仅能查看'),
                        onOk: () => {
                            self.isShowingConvertException = false;
                        },
                        cancelText: null,
                    });
                }, 10);
            }
        }
    }

    public setUserPriviledgeServiceDelegate(userPriviledgeService: IUserPriviledgeService): void {
        if (this.userPriviledgeService) return;
        this.userPriviledgeService = userPriviledgeService;
        autorun(() => {
            if (!this.userPriviledgeService) {
                return;
            }
            const canShowEditTips = this.userPriviledgeService.pptEditorState > PPTEditorStateType.EDITABLE;
            if (canShowEditTips) {
                this.hasShownCannotEditTips = false;
            }
        });
    }

    public async setAnonymousViewServiceDelegate(dynamicAnonymousViewService: IDynamicService<IAnonymousViewService>) {
        const anonymousViewService =  await dynamicAnonymousViewService.promise();
        this.anonymousViewService = anonymousViewService;
    }

    // 是否展示隐身查看的弹窗
    private isShowAnonymousModal(): boolean {
        if (!this.anonymousViewService?.anonymousStatus) return false;
        return this.userPriviledgeService?.fileAuth === PPTEditorStateEnumType.EDITABLE;
    }

    /** 用户具备文档权限，但被业务限制了编辑器状态 */
    private isEditorStateChangedByOther(): boolean {
        const { userPriviledgeService } = this;
        if (!userPriviledgeService) return false;

        return userPriviledgeService.fileAuth === PPTEditorStateEnumType.EDITABLE
            && userPriviledgeService.pptEditorState !== PPTEditorStateEnumType.EDITABLE;
    }
}
